import React, { useState, useEffect, useCallback } from 'react'
import { handleUnknownError } from '@news-mono/common'
import { useRecaptcha } from '../../recaptcha'
import {
    StyledNewsTipsContainer,
    StyledNewsTipsHeading,
    StyledNewsTipsDescription,
    StyledForm,
    StyledFieldGroup,
    StyledLabel,
    StyledInput,
    StyledSelect,
    StyledTextarea,
    StyledCheckboxGroup,
    StyledCheckboxInput,
    StyledCheckboxLabel,
    StyledSubmitButton,
    StyledStatusMessage,
    StyledHoneypot,
    StyledPrivacyDisclosure,
    StyledFieldError,
} from './NewsTipsForm.styled'

const MAX_NAME_LENGTH = 200
const MAX_EMAIL_LENGTH = 320
const MAX_PHONE_LENGTH = 50
const MAX_SUBURB_LENGTH = 100
const MAX_MESSAGE_LENGTH = 5000
const COOLDOWN_SECONDS = 30
const EMAIL_REGEX =
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/

const AUSTRALIAN_STATES = [
    'NSW',
    'VIC',
    'QLD',
    'WA',
    'SA',
    'TAS',
    'ACT',
    'NT',
] as const

type AustralianState = typeof AUSTRALIAN_STATES[number] | ''

interface FormData {
    name: string
    email: string
    phone: string
    suburb: string
    state: AustralianState
    message: string
    urgent: boolean
}

interface FieldErrors {
    name?: string
    email?: string
    phone?: string
    suburb?: string
    state?: string
    message?: string
}

type FormStatus = 'idle' | 'submitting' | 'success' | 'error'

function validateForm(data: FormData): FieldErrors {
    const errors: FieldErrors = {}

    if (!data.name.trim()) {
        errors.name = 'Name is required'
    }

    const email = data.email.trim()

    if (!email) {
        errors.email = 'Email is required'
    } else if (
        email.length > MAX_EMAIL_LENGTH ||
        !EMAIL_REGEX.test(email)
    ) {
        errors.email = 'Email must be a valid email address'
    }
    if (!data.phone.trim()) {
        errors.phone = 'Phone is required'
    }

    if (!data.state) {
        errors.state = 'State is required'
    }

    if (!data.message.trim()) {
        errors.message = 'Message is required'
    }

    return errors
}

export const NewsTipsForm: React.FC = () => {
    const [formData, setFormData] = useState<FormData>({
        name: '',
        email: '',
        phone: '',
        suburb: '',
        state: '',
        message: '',
        urgent: false,
    })
    const [fieldErrors, setFieldErrors] = useState<FieldErrors>({})
    const [honeypot, setHoneypot] = useState('')
    const [status, setStatus] = useState<FormStatus>('idle')
    const [cooldown, setCooldown] = useState(0)
    const { executeRecaptcha } = useRecaptcha()

    useEffect(() => {
        if (cooldown <= 0) return
        const timer = setInterval(() => {
            setCooldown((prev) => prev - 1)
        }, 1000)
        return () => clearInterval(timer)
    }, [cooldown])

    const handleChange = useCallback(
        (
            e: React.ChangeEvent<
                HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
            >,
        ) => {
            const { name, value } = e.target
            setFormData((prev) => ({ ...prev, [name]: value }))
            setFieldErrors((prev) => ({ ...prev, [name]: undefined }))
        },
        [],
    )

    const handleUrgentChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const { checked } = e.target
            setFormData((prev) => ({ ...prev, urgent: checked }))
        },
        [],
    )

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()

        if (honeypot) {
            setStatus('success')
            return
        }

        const errors = validateForm(formData)
        if (Object.keys(errors).length > 0) {
            setFieldErrors(errors)
            return
        }

        setFieldErrors({})
        setStatus('submitting')

        try {
            const token = await executeRecaptcha({
                action: 'news_tip_submit',
            })

            const response = await fetch('/news-tip-submit', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    token,
                    name: formData.name.trim(),
                    email: formData.email.trim(),
                    phone: formData.phone.trim(),
                    suburb: formData.suburb.trim(),
                    state: formData.state,
                    message: formData.message.trim(),
                    urgent: formData.urgent,
                }),
            })

            if (response.ok) {
                setStatus('success')
                setFormData({
                    name: '',
                    email: '',
                    phone: '',
                    suburb: '',
                    state: '',
                    message: '',
                    urgent: false,
                })
                setCooldown(COOLDOWN_SECONDS)
            } else {
                setStatus('error')
            }
        } catch (error) {
            const err = handleUnknownError(error)
            console.error(err)
            setStatus('error')
        }
    }

    if (status === 'success') {
        return (
            <StyledNewsTipsContainer>
                <StyledStatusMessage variant="success">
                    Thank you for your submission. Our team will review your tip
                    shortly.
                </StyledStatusMessage>
            </StyledNewsTipsContainer>
        )
    }

    return (
        <StyledNewsTipsContainer>
            <StyledNewsTipsHeading>Send Us a News Tip</StyledNewsTipsHeading>
            <StyledNewsTipsDescription>
                Have a story or information you think we should know about? Fill
                in the form below and our newsroom team will review it.
            </StyledNewsTipsDescription>

            <StyledForm onSubmit={handleSubmit} noValidate>
                <StyledFieldGroup>
                    <StyledLabel htmlFor="newsTip-name">Name</StyledLabel>
                    <StyledInput
                        id="newsTip-name"
                        name="name"
                        type="text"
                        required
                        aria-invalid={!!fieldErrors.name}
                        aria-describedby={
                            fieldErrors.name ? 'newsTip-name-error' : undefined
                        }
                        maxLength={MAX_NAME_LENGTH}
                        placeholder="Your name"
                        value={formData.name}
                        onChange={handleChange}
                    />
                    {fieldErrors.name && (
                        <StyledFieldError id="newsTip-name-error" role="alert">
                            {fieldErrors.name}
                        </StyledFieldError>
                    )}
                </StyledFieldGroup>

                <StyledFieldGroup>
                    <StyledLabel htmlFor="newsTip-phone">Phone</StyledLabel>
                    <StyledInput
                        id="newsTip-phone"
                        name="phone"
                        type="tel"
                        required
                        aria-invalid={!!fieldErrors.phone}
                        aria-describedby={
                            fieldErrors.phone
                                ? 'newsTip-phone-error'
                                : undefined
                        }
                        maxLength={MAX_PHONE_LENGTH}
                        placeholder="Your phone number"
                        value={formData.phone}
                        onChange={handleChange}
                    />
                    {fieldErrors.phone && (
                        <StyledFieldError id="newsTip-phone-error" role="alert">
                            {fieldErrors.phone}
                        </StyledFieldError>
                    )}
                </StyledFieldGroup>

                <StyledFieldGroup>
                    <StyledLabel htmlFor="newsTip-email">Email</StyledLabel>
                    <StyledInput
                        id="newsTip-email"
                        name="email"
                        type="email"
                        required
                        aria-invalid={!!fieldErrors.email}
                        aria-describedby={
                            fieldErrors.email
                                ? 'newsTip-email-error'
                                : undefined
                        }
                        maxLength={MAX_EMAIL_LENGTH}
                        placeholder="Your email address"
                        value={formData.email}
                        onChange={handleChange}
                    />
                    {fieldErrors.email && (
                        <StyledFieldError id="newsTip-email-error" role="alert">
                            {fieldErrors.email}
                        </StyledFieldError>
                    )}
                </StyledFieldGroup>

                <StyledFieldGroup>
                    <StyledLabel htmlFor="newsTip-suburb">Suburb</StyledLabel>
                    <StyledInput
                        id="newsTip-suburb"
                        name="suburb"
                        type="text"
                        aria-invalid={!!fieldErrors.suburb}
                        aria-describedby={
                            fieldErrors.suburb
                                ? 'newsTip-suburb-error'
                                : undefined
                        }
                        maxLength={MAX_SUBURB_LENGTH}
                        placeholder="Your suburb"
                        value={formData.suburb}
                        onChange={handleChange}
                    />
                    {fieldErrors.suburb && (
                        <StyledFieldError
                            id="newsTip-suburb-error"
                            role="alert"
                        >
                            {fieldErrors.suburb}
                        </StyledFieldError>
                    )}
                </StyledFieldGroup>

                <StyledFieldGroup>
                    <StyledLabel htmlFor="newsTip-state">State</StyledLabel>
                    <StyledSelect
                        id="newsTip-state"
                        name="state"
                        required
                        aria-invalid={!!fieldErrors.state}
                        aria-describedby={
                            fieldErrors.state
                                ? 'newsTip-state-error'
                                : undefined
                        }
                        value={formData.state}
                        onChange={handleChange}
                    >
                        <option value="">Select your state</option>
                        {AUSTRALIAN_STATES.map((state) => (
                            <option key={state} value={state}>
                                {state}
                            </option>
                        ))}
                    </StyledSelect>
                    {fieldErrors.state && (
                        <StyledFieldError id="newsTip-state-error" role="alert">
                            {fieldErrors.state}
                        </StyledFieldError>
                    )}
                </StyledFieldGroup>

                <StyledFieldGroup>
                    <StyledLabel htmlFor="newsTip-message">
                        Your News Tip
                    </StyledLabel>
                    <StyledTextarea
                        id="newsTip-message"
                        name="message"
                        required
                        aria-invalid={!!fieldErrors.message}
                        aria-describedby={
                            fieldErrors.message
                                ? 'newsTip-message-error'
                                : undefined
                        }
                        maxLength={MAX_MESSAGE_LENGTH}
                        placeholder="Tell us what you know..."
                        value={formData.message}
                        onChange={handleChange}
                    />
                    {fieldErrors.message && (
                        <StyledFieldError
                            id="newsTip-message-error"
                            role="alert"
                        >
                            {fieldErrors.message}
                        </StyledFieldError>
                    )}
                </StyledFieldGroup>

                <StyledCheckboxGroup>
                    <StyledCheckboxInput
                        id="newsTip-urgent"
                        name="urgent"
                        type="checkbox"
                        checked={formData.urgent}
                        onChange={handleUrgentChange}
                    />
                    <StyledCheckboxLabel htmlFor="newsTip-urgent">
                        This tip is urgent
                    </StyledCheckboxLabel>
                </StyledCheckboxGroup>

                <StyledHoneypot aria-hidden="true">
                    <label htmlFor="newsTip-address">
                        Leave this field empty
                    </label>
                    <input
                        id="newsTip-address"
                        name="address"
                        type="text"
                        tabIndex={-1}
                        autoComplete="off"
                        value={honeypot}
                        onChange={(e) => setHoneypot(e.target.value)}
                    />
                </StyledHoneypot>

                {status === 'error' && (
                    <StyledStatusMessage variant="error">
                        Something went wrong. Please try again.
                    </StyledStatusMessage>
                )}

                <StyledPrivacyDisclosure>
                    We collect your information so our newsroom can review and
                    respond to your tip. Read Seven&apos;s{' '}
                    <a
                        href="https://sevenwestmedia.com.au/privacy-policies"
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        Privacy Policy
                    </a>
                    .
                </StyledPrivacyDisclosure>

                <StyledSubmitButton
                    type="submit"
                    disabled={status === 'submitting' || cooldown > 0}
                >
                    {status === 'submitting'
                        ? 'Submitting...'
                        : cooldown > 0
                        ? `Resubmit in ${cooldown}s`
                        : 'Submit Tip'}
                </StyledSubmitButton>
            </StyledForm>
        </StyledNewsTipsContainer>
    )
}
